"use client";

import React, { useState } from "react";
import firebaseApp from "@src/auth/lib/firebaseClient";
import { getAuth } from "firebase/auth";
import { StatefulPopover, PLACEMENT } from "baseui/popover";
import { Items, StatefulMenu } from "baseui/menu";
import { Button, KIND, SHAPE } from "baseui/button";
import { useRouter } from "next/router";
import { useAuth } from "@src/common/hooks/useAuth";
import {
  UserIcon,
  ComputerDesktopIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/outline";
import { UserRoleType } from "@api/graphql/generated/generated-types";
import commonRoutes from "@src/common/lib/routing/commonRoutes";
import {
  Theme,
  useTheme,
} from "@src/common/components/ThemeProvider/ThemeProvider";

interface ThemeOption {
  key: string;
  label: string;
  icon: React.ReactNode;
  theme: Theme;
}

const NavActions: React.FC = () => {
  const router = useRouter();
  const auth = getAuth(firebaseApp);
  const { courier, courieUser, isRoleType, employeeId } = useAuth();
  const logoUrl = courier?.courier?.logoUrl;
  const { theme, setTheme, effectiveTheme } = useTheme();
  const [isThemeOpen, setIsThemeOpen] = useState(false);

  const handleSignOut = async () => {
    await auth.signOut();
  };

  const menuItems: Items = [
    ...(employeeId ? [{ label: "Admin Dashboard" }] : []),
    ...(isRoleType(UserRoleType.Administrator)
      ? [{ label: "Company Settings" }]
      : []),
    { label: "Sign Out" },
  ];

  const themeOptions: ThemeOption[] = [
    {
      key: Theme.AUTO,
      label: "System",
      icon: <ComputerDesktopIcon className="w-4 h-4" />,
      theme: Theme.AUTO,
    },
    {
      key: Theme.LIGHT,
      label: "Light",
      icon: <SunIcon className="w-4 h-4" />,
      theme: Theme.LIGHT,
    },
    {
      key: Theme.DARK,
      label: "Dark",
      icon: <MoonIcon className="w-4 h-4" />,
      theme: Theme.DARK,
    },
  ];

  const getCurrentThemeIcon = () => {
    if (effectiveTheme === Theme.DARK) {
      return <MoonIcon className="w-5 h-5" />;
    }
    return <SunIcon className="w-5 h-5" />;
  };

  const renderThemeMenuItems = () => {
    return themeOptions.map((option) => ({
      ...option,
      label: (
        <div className="flex items-center gap-3 py-0.5">
          <div
            className={`transition-colors duration-200 ${
              theme === option.theme
                ? "text-blue-600 dark:text-blue-400"
                : "text-gray-600 dark:text-gray-400"
            }`}
          >
            {option.icon}
          </div>
          <span
            className={`text-sm font-medium transition-colors duration-200 ${
              theme === option.theme
                ? "text-gray-900 dark:text-gray-100"
                : "text-gray-700 dark:text-gray-300"
            }`}
          >
            {option.label}
          </span>
          {theme === option.theme && (
            <div className="ml-auto">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400 transition-all duration-200" />
            </div>
          )}
        </div>
      ),
    }));
  };

  const handleThemeSelectionChange = (selectedItem: any) => {
    setTheme(selectedItem.theme);
  };

  return (
    <div className="flex items-center gap-2">
      {logoUrl && <img src={logoUrl} className="h-8" alt="Logo" />}
      <StatefulPopover
        focusLock
        placement={PLACEMENT.bottomRight}
        onOpen={() => setIsThemeOpen(true)}
        onClose={() => setIsThemeOpen(false)}
        overrides={{
          Body: {
            style: { zIndex: 50 },
          },
          Inner: {
            style: {
              backgroundColor: "transparent",
              padding: "0",
            },
            props: {
              className: "",
            },
          },
        }}
        content={({ close }) => (
          <StatefulMenu
            items={renderThemeMenuItems()}
            onItemSelect={({ item }) => {
              handleThemeSelectionChange(item);
              close();
            }}
            overrides={{
              List: {
                style: {
                  width: "160px",
                  backgroundColor: "white",
                  borderRadius: "12px",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow:
                    "0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
                  padding: "6px",
                  overflow: "hidden",
                },
                props: {
                  className:
                    "dark:bg-gray-800 dark:border-gray-700 dark:shadow-xl",
                },
              },
              Option: {
                style: ({ $isHighlighted, $isFocused }) => ({
                  backgroundColor:
                    $isHighlighted || $isFocused
                      ? "rgba(59, 130, 246, 0.1)"
                      : "transparent",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  margin: "2px 0",
                  transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                }),
                props: {
                  className:
                    "dark:hover:bg-gray-700/50 transition-all duration-150",
                },
              },
            }}
          />
        )}
      >
        <Button
          kind={KIND.tertiary}
          shape={SHAPE.default}
          overrides={{
            BaseButton: {
              style: {
                height: "40px",
                paddingLeft: "12px",
                paddingRight: "12px",
                borderRadius: "10px",
                backgroundColor: "transparent",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              },
              props: {
                className:
                  "hover:bg-gray-50 dark:hover:bg-gray-800/50 dark:border-gray-700 dark:bg-transparent",
              },
            },
          }}
        >
          <div className="text-gray-700 dark:text-gray-300 transition-colors duration-200">
            {getCurrentThemeIcon()}
          </div>
        </Button>
      </StatefulPopover>
      <StatefulPopover
        focusLock
        placement={PLACEMENT.bottomRight}
        content={({ close }) => (
          <StatefulMenu
            items={menuItems}
            onItemSelect={({ item }) => {
              if (item.label === "Sign Out") {
                handleSignOut();
              }
              if (item.label === "Company Settings") {
                router.push("/settings");
              }
              if (item.label === "Admin Dashboard") {
                router.push(commonRoutes.dashboard);
              }
              close();
            }}
            overrides={{
              List: {
                style: { width: "100px" },
              },
            }}
          />
        )}
        overrides={{
          Body: {
            style: {
              zIndex: 100,
            },
          },
        }}
      >
        <div className="flex items-center rounded-md hover:cursor-pointer">
          <Button
            shape={SHAPE.circle}
            style={{ height: 36, width: 36 }}
            title={courieUser?.email}
          >
            <UserIcon className="w-4" />
          </Button>
        </div>
      </StatefulPopover>
    </div>
  );
};

export default NavActions;
